import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import type { TimeLog, TodayResponse, TeamMember, BreakType } from '../types';

// ─── Queries ────────────────────────────────────────────────────────────────

export function useTodayLog() {
  return useQuery({
    queryKey: ['attendance', 'today'],
    queryFn: async () => {
      const { data } = await api.get<TodayResponse>('/api/time-logs/today');
      return data;
    },
  });
}

export function useMyLogs(limit = 20) {
  return useQuery({
    queryKey: ['attendance', 'logs', limit],
    queryFn: async () => {
      const { data } = await api.get<{ logs: TimeLog[] }>(`/api/time-logs?limit=${limit}`);
      return data.logs ?? [];
    },
    placeholderData: keepPreviousData,
  });
}

/** Team roster (today or selected date) — privileged roles only. */
export function useTeamAttendance(enabled: boolean, dept?: string, date?: string) {
  return useQuery({
    queryKey: ['attendance', 'team', dept || 'all', date || 'today'],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (dept && dept !== 'all') params.append('dept', dept);
      if (date) params.append('date', date);
      const suffix = params.toString() ? `?${params.toString()}` : '';
      const { data } = await api.get<{ team: TeamMember[] }>(`/api/time-logs/team${suffix}`);
      return data.team ?? [];
    },
    enabled,
  });
}

/** Overall time logs across all employees / date range — privileged roles or self. */
export function useAllTimeLogs(params?: { userId?: string; dept?: string; from?: string; to?: string; limit?: number }, enabled = true) {
  return useQuery({
    queryKey: ['attendance', 'all-time-logs', params],
    queryFn: async () => {
      const q = new URLSearchParams();
      if (params?.userId) q.append('userId', params.userId);
      if (params?.dept && params.dept !== 'all') q.append('dept', params.dept);
      if (params?.from) q.append('from', params.from);
      if (params?.to) q.append('to', params.to);
      if (params?.limit) q.append('limit', String(params.limit));
      const suffix = q.toString() ? `?${q.toString()}` : '';
      const { data } = await api.get<{ logs: (TimeLog & { display_name?: string; department?: string; role?: string })[] }>(`/api/time-logs${suffix}`);
      return data.logs ?? [];
    },
    enabled,
    placeholderData: keepPreviousData,
  });
}

// ─── Mutations ───────────────────────────────────────────────────────────────

export function useAttendanceMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => Promise.all([
    queryClient.invalidateQueries({ queryKey: ['attendance'] }),
  ]);

  const checkIn = useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{ ok: boolean; time: string }>('/api/time-logs/checkin');
      return data;
    },
    onSuccess: invalidate,
  });

  const checkOut = useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{ ok: boolean; time: string; hours: number }>('/api/time-logs/checkout');
      return data;
    },
    onSuccess: invalidate,
  });

  const startBreak = useMutation({
    mutationFn: async (breakType: BreakType) => {
      const { data } = await api.post<{ ok: boolean; id: string }>('/api/time-logs/breaks/start', { breakType });
      return data;
    },
    onSuccess: invalidate,
  });

  const endBreak = useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{ ok: boolean; duration: number }>('/api/time-logs/breaks/end');
      return data;
    },
    onSuccess: invalidate,
  });

  return { checkIn, checkOut, startBreak, endBreak };
}
